import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ActivityAsyncAdapter } from 'app/model/adapters/activities/activity.adapter';

import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { VariablesConfig } from '../../pipeline/variables-config';
import { Owned } from 'app/model/base/named';
import { AppendVariableActivity } from 'app/model/pipeline/variable-append.activity';
import { IAppendVariableActivityContext } from 'app/interfaces/copilot.pipeline.action.types';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AppendVariableActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        type: '*',
        description: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: '*'
    }
})
export class AppendVariablesActivityAdapter extends ActivityAsyncAdapter<AppendVariableActivity, Model.AppendVariableActivity, IAppendVariableActivityContext> {

    public model(): IModel {
        return Metamodel.modelFor(AppendVariableActivity);
    }

    public override async toFabricCopilotContext(activity: AppendVariableActivity): Promise<IAppendVariableActivityContext> {
        if (activity) {
            const activityContext: IAppendVariableActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.variables) {
                activityContext.VariableName = activity.variables.name;
                activityContext.VariableValue = ValueAdapter.toResource(activity.variables, VariablesConfig.VALUE, true);
                if (activityContext.VariableValue !== undefined && typeof activityContext.VariableValue !== 'string') {
                    activityContext.VariableValue = JSON.stringify(activityContext.VariableValue);
                }
            }
            return activityContext;
        }
        return undefined;
    }
    
    public override async fromFabricCopilotContext(
        context: IAppendVariableActivityContext,
        model?: AppendVariableActivity
    ): Promise<AppendVariableActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.VariableName && context.VariableValue) {
                ValueAdapter.toModel(model, AppendVariableActivity.VALUE, context.VariableValue);
                const variableConfig = Owned.create<VariablesConfig>(VariablesConfig.MODEL, model);
                variableConfig.name = context.VariableName;
                ValueAdapter.toModel(model, VariablesConfig.VALUE, context.VariableValue);                
                model.variables = variableConfig;
            }
        }
        return model;
    }

    protected async _toResource(activity: AppendVariableActivity): Promise<Model.AppendVariableActivity> {
        const modelVariableActivity: Model.AppendVariableActivity = super._getCommonActivityProperties(activity, 'AppendVariable') as Model.SetVariableActivity;

        if (activity.variables) {
            const expressionValue = ValueAdapter.toResource(activity, AppendVariableActivity.MODEL.attribute('value'));
            if (typeof expressionValue === 'object') {
                modelVariableActivity.typeProperties = {
                    variableName: activity.variables.name,
                    value: expressionValue
                };
            } else {
                modelVariableActivity.typeProperties = {
                    variableName: activity.variables.name,
                    value: activity.variables.value
                };
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelVariableActivity, parsed);
        }

        return modelVariableActivity;
    }

    protected async _toModel(modelActivity: Model.AppendVariableActivity): Promise<AppendVariableActivity> {
        const activity: AppendVariableActivity = AppendVariableActivity.MODEL.create<AppendVariableActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);

        const variableConfig = Owned.create<VariablesConfig>(VariablesConfig.MODEL, activity);
        if (modelActivity.typeProperties) {
            variableConfig.name = modelActivity.typeProperties.variableName;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let varObj = null;
            if (activity.variables) {
                varObj = this.toVariableObject(activity.variables);
                variableConfig.value = varObj.value;
            } else {
                variableConfig.value = modelActivity.typeProperties.value;
            }

            ValueAdapter.toModel(activity, AppendVariableActivity.MODEL.attribute('value'), modelActivity.typeProperties.value);
        }
        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, AppendVariableActivity.MODEL));
        activity.variables = variableConfig;
        return activity;
    }

    private toVariableObject(varConfig: VariablesConfig): Model.AppendVariableActivityTypeProperties {
        return ValueAdapter.toResource(varConfig, AppendVariableActivity.MODEL.attribute('variables'));
    }
}
